<template>
  <md-dialog
    id="traceabilityCollectionDateModal"
    ref="traceabilityCollectionDateModal"
    :md-esc-to-close="false"
    :md-click-outside-to-close="false"
    class="traceability-collection-date-modal"
  >
    <md-dialog-title>
      <button
        id="mod_btnClose"
        class="close-button btn-transparent"
        @click="closeModal"
      >
        <md-icon>clear</md-icon>
      </button>
      <span>{{ title }}</span>
    </md-dialog-title>
    <md-dialog-content>
      <div class="mt-4">
        <div
          v-if="isLoadingCollectDate"
          class="loading-container"
        >
          <spinner />
        </div>
        <ap-datepicker
          v-else
          :key="datepickerKey"
          class="ap-datepicker-style"
          :date.sync="collectionDate"
          :has-time.sync="hasTime"
          :range="false"
          placeholder="Selecione data e hora da coleta"
          :disabled-date="disablePastDates"
        />
      </div>
    </md-dialog-content>
    <md-dialog-actions class="px-7">
      <ButtonBase
        variant="quarternary"
        @click="closeModal"
      >
        Cancelar
      </ButtonBase>
      <ButtonBase
        variant="primary"
        :loading="isUpdating"
        @click="submitModal"
      >
        Salvar
      </ButtonBase>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import ApDatepicker from '@/components/apoio/ApDatepicker.vue'
import Spinner from '@/components/common/Spinner'
import notify from '@/api/notification'
import moment from 'moment'
import orderApi from '@/api/order'

export default {
  name: 'TraceabilityCollectionDateModal',

  components: {
    ApDatepicker,
    Spinner,
  },

  props: {
    title: {
      type: String,
      default: 'Atualizar Data e Hora da Coleta',
    },
    value: {
      type: Object,
      default: () => ({ date: null }),
    },
    initialDate: {
      type: [String, Date],
      default: null,
    },
    initialTime: {
      type: [String, Date],
      default: null,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [String, Number],
      default: null,
    },
  },

  emits: ['update'],

  data() {
    return {
      collectionDate: null,
      hasTime: true,
      datepickerKey: 0,
      isLoadingCollectDate: false,
      isUpdating: false,
    }
  },

  computed: {
    disablePastDates() {
      return date => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        const minDate = new Date(today)
        minDate.setDate(today.getDate() - 10)

        const maxDate = new Date(today)
        maxDate.setDate(today.getDate() + 15)

        const compareDate = new Date(date)
        compareDate.setHours(0, 0, 0, 0)

        return compareDate < minDate || compareDate > maxDate
      }
    },
  },

  watch: {
    showModal(newValue) {
      if (newValue) {
        this.openModal()
      } else {
        this.closeModal()
      }
    },

    initialDate: {
      handler() {
        if (
          this.$refs.traceabilityCollectionDateModal &&
          this.$refs.traceabilityCollectionDateModal.$el.style.display !== 'none'
        ) {
          this.setupInitialDate()
        }
      },
      immediate: false,
    },

    initialTime: {
      handler() {
        if (
          this.$refs.traceabilityCollectionDateModal &&
          this.$refs.traceabilityCollectionDateModal.$el.style.display !== 'none'
        ) {
          this.setupInitialDate()
        }
      },
      immediate: false,
    },
  },

  methods: {
    ...mapActions([]),

    async fetchCollectDate() {
      try {
        if (!this.orderId) {
          console.warn('orderId não fornecido para buscar data de coleta')
          return
        }

        this.isLoadingCollectDate = true
        const response = await orderApi.getCollectDate(this.orderId)

        if (response?.data?.collectDate) {
          const collectDateIso = response.data.collectDate

          const parsedDate = moment(collectDateIso)
          if (parsedDate.isValid()) {
            this.collectionDate = parsedDate.toDate()
            this.hasTime = true
            this.datepickerKey++

            this.$nextTick(() => {
              this.$forceUpdate()
            })
          }
        }
      } catch (error) {
        console.error('Erro ao buscar data de coleta:', error)
      } finally {
        this.isLoadingCollectDate = false
      }
    },
    setupInitialDate() {
      this.collectionDate = null
      this.hasTime = true

      if (this.initialDate || this.value?.date) {
        let dateToUse

        if (this.initialDate && this.initialTime) {
          const dateStr = this.initialDate
          const timeStr = this.initialTime

          const combinedDateTime = moment(`${dateStr} ${timeStr}`, 'DD/MM/YYYY HH:mm')
          if (combinedDateTime.isValid()) {
            dateToUse = combinedDateTime.toDate()
          }
        } else if (this.initialDate) {
          const parsedDate = moment(this.initialDate, 'DD/MM/YYYY')
          if (parsedDate.isValid()) {
            dateToUse = parsedDate.toDate()
          }
        } else if (this.value?.date) {
          dateToUse = new Date(this.value.date)
        }

        if (dateToUse && !isNaN(dateToUse.getTime())) {
          this.collectionDate = dateToUse

          this.datepickerKey++

          this.$nextTick(() => {
            this.$forceUpdate()
          })
        }
      }
    },

    async openModal() {
      await this.fetchCollectDate()

      if (!this.collectionDate) {
        this.setupInitialDate()
      }

      this.$refs.traceabilityCollectionDateModal.open()
    },

    closeModal() {
      this.$refs.traceabilityCollectionDateModal.close()
    },

    async submitModal() {
      if (!this.isValidDate() || this.isUpdating) {
        return
      }

      try {
        this.isUpdating = true
        await this.emitUpdate()
        this.closeModal()
      } catch (error) {
        console.error('Erro ao atualizar data de coleta:', error)
      } finally {
        this.isUpdating = false
      }
    },

    isValidDate() {
      let selectedDate

      if (this.collectionDate) {
        selectedDate = new Date(this.collectionDate)
      } else {
        notify({
          title: 'AVISO:',
          message: 'Por favor, selecione uma data para a coleta.',
        })
        return false
      }

      if (isNaN(selectedDate.getTime())) {
        notify({
          title: 'AVISO:',
          message: 'Data inválida selecionada.',
        })
        return false
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const minDate = new Date(today)
      minDate.setDate(today.getDate() - 10)

      const maxDate = new Date(today)
      maxDate.setDate(today.getDate() + 15)

      const selectedDateOnly = new Date(selectedDate)
      selectedDateOnly.setHours(0, 0, 0, 0)

      if (selectedDateOnly < minDate) {
        notify({
          title: 'AVISO:',
          message: 'A data da coleta não pode ser anterior a 10 dias atrás.',
        })
        return false
      }

      if (selectedDateOnly > maxDate) {
        notify({
          title: 'AVISO:',
          message: 'A data da coleta não pode ser posterior a 15 dias no futuro.',
        })
        return false
      }

      if (this.hasTime && selectedDateOnly.getTime() === today.getTime()) {
        const now = new Date()
        if (selectedDate <= now) {
          notify({
            title: 'AVISO:',
            message: 'Para o dia atual, o horário selecionado deve ser no futuro.',
          })
          return false
        }
      }

      return true
    },

    emitUpdate() {
      if (!this.collectionDate) {
        return Promise.reject(new Error('Data de coleta não selecionada'))
      }

      const selectedDate = new Date(this.collectionDate)

      const momentDate = moment(selectedDate)
      const date = momentDate.format('DD/MM/YYYY')
      const time = momentDate.format('HH:mm')

      const payload = {
        date: date,
        time: time,
        dataColeta: selectedDate.toISOString(),
      }

      return new Promise((resolve, reject) => {
        this.$emit('update', {
          ...payload,
          resolve,
          reject,
        })
      })
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.md-dialog) {
  max-width: 600px;
  width: 100%;
  overflow: visible !important;

  .md-dialog-content {
    overflow: visible !important;
  }
}

:deep(.ap-datepicker-style) {
  width: 100% !important;
  input {
    height: 50px !important;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;

  .loading-text {
    margin-top: 10px;
    color: #666;
    font-size: 14px;
  }
}

.md-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
